import { Link, useParams, Navigate } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Clock, Check, ShieldCheck, Sparkles, Clock as ClockIcon,
  Wind, Armchair, Gem, Car, Droplets,
} from "lucide-react";
import { getService, SERVICES } from "@/data/services";
import { Reveal } from "@/components/common/Reveal";
import Seo, { BreadcrumbJsonLd } from "@/components/common/Seo";

const ICONS = { ShieldCheck, Sparkles, Clock: ClockIcon, Wind, Armchair, Gem, Car, Droplets };

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);
  if (!service) return <Navigate to="/services" replace />;

  const others = SERVICES.filter((s) => s.slug !== slug);

  return (
    <div data-testid="service-detail-page">
      <Seo
        title={`${service.name} in Ottawa — ${service.price} | Reverence Autos`}
        description={`${service.intro} Mobile ${service.name.toLowerCase()} across Ottawa — we come to you.`}
        path={`/services/${service.slug}`}
        image={service.hero}
      />
      <BreadcrumbJsonLd items={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: service.name, path: `/services/${service.slug}` }]} />

      {/* Hero */}
      <section className="relative overflow-hidden bg-brand-charcoal">
        <img src={service.hero} alt={`${service.name} — Reverence Autos`} className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-charcoal via-brand-charcoal/85 to-brand-charcoal/40" />
        <div className="container-rv relative py-24 lg:py-32">
          <Reveal className="max-w-2xl">
            <Link to="/services" data-testid="service-back-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-silver transition-colors hover:text-white">
              <ArrowLeft className="h-4 w-4" /> All services
            </Link>
            <p className="mt-6 overline text-brand-red">{service.tagline}</p>
            <h1 className="mt-3 font-serif text-4xl font-bold leading-[1.05] text-white sm:text-5xl lg:text-6xl">{service.name}</h1>
            <p className="mt-5 max-w-xl text-brand-silver">{service.intro}</p>
            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3">
              <div>
                <p className="font-serif text-3xl font-bold text-white">{service.price}</p>
                <p className="text-xs text-brand-silver">{service.priceNote}</p>
              </div>
              <span className="hidden h-10 w-px bg-white/20 sm:block" />
              <p className="flex items-center gap-2 text-sm text-white/85"><Clock className="h-4 w-4 text-brand-red" /> {service.duration}</p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to={`/book?service=${service.id}`} data-testid="service-book-cta" className="btn-red">
                Book the {service.name.split(" ")[0]} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/pricing" className="btn-outline-light">Compare pricing</Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Why it matters */}
      <section className="bg-white">
        <div className="container-rv grid gap-12 py-20 lg:grid-cols-[1fr_1.1fr] lg:py-28">
          <Reveal>
            <p className="overline text-brand-red">Why it matters</p>
            <h2 className="mt-4 font-serif text-3xl font-bold leading-tight text-brand-charcoal sm:text-4xl">
              More than clean — cared for
            </h2>
            <p className="mt-5 text-brand-muted">{service.why}</p>
            <div className="mt-8 rounded-2xl border border-brand-silver/40 bg-brand-offwhite p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-brand-red">Best for</p>
              <p className="mt-1.5 text-sm text-brand-charcoal/85">{service.bestFor}</p>
            </div>
          </Reveal>

          <div className="grid gap-5 sm:grid-cols-1">
            {service.benefits.map((b, i) => {
              const Icon = ICONS[b.icon] || Sparkles;
              return (
                <Reveal key={b.title} delay={i * 0.07}>
                  <div className="flex gap-4 rounded-2xl border border-brand-silver/40 bg-white p-5 transition-shadow duration-300 hover:shadow-[0_24px_60px_-45px_rgba(26,28,30,0.5)]">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-red/10 text-brand-red">
                      <Icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-serif text-lg font-bold text-brand-charcoal">{b.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-brand-muted">{b.desc}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="bg-brand-offwhite">
        <div className="container-rv py-20 lg:py-28">
          <Reveal className="mb-12 max-w-2xl">
            <p className="overline text-brand-red">What's included</p>
            <h2 className="mt-4 font-serif text-3xl font-bold leading-tight text-brand-charcoal sm:text-4xl">
              Every step, and the reason behind it
            </h2>
          </Reveal>
          <div className="grid gap-x-8 gap-y-5 md:grid-cols-2">
            {service.included.map((s, i) => (
              <Reveal key={s.step} delay={(i % 2) * 0.06}>
                <div className="flex gap-4 rounded-2xl border border-brand-silver/40 bg-white p-5">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-red font-serif text-sm font-bold text-white">{i + 1}</span>
                  <div>
                    <h3 className="font-serif text-lg font-bold leading-tight text-brand-charcoal">{s.step}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-brand-muted">{s.why}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery strip */}
      {service.gallery?.length > 0 && (
        <section className="bg-white">
          <div className="container-rv py-16">
            <div className="grid gap-4 sm:grid-cols-3">
              {service.gallery.map((g, i) => (
                <Reveal key={g + i} delay={i * 0.06}>
                  <div className="aspect-[4/3] overflow-hidden rounded-2xl">
                    <img src={g} alt={`${service.name} result ${i + 1}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA band */}
      <section className="bg-brand-charcoal">
        <div className="container-rv flex flex-wrap items-center gap-6 py-16">
          <div className="flex-1">
            <h2 className="font-serif text-3xl font-bold text-white">Ready for the {service.name}?</h2>
            <p className="mt-2 text-sm text-brand-silver">We come to your driveway anywhere in Ottawa — pick a date and we'll handle the rest.</p>
          </div>
          <Link to={`/book?service=${service.id}`} className="btn-red" data-testid="service-cta-bottom">
            Schedule a Detailing Now <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Other services */}
      <section className="bg-white">
        <div className="container-rv py-20">
          <h2 className="mb-8 font-serif text-2xl font-bold text-brand-charcoal">Explore other services</h2>
          <div className="grid gap-5 sm:grid-cols-3">
            {others.map((o) => (
              <Link key={o.slug} to={`/services/${o.slug}`} data-testid={`service-related-${o.id}`}
                className="group flex items-center gap-4 rounded-2xl border border-brand-silver/40 bg-white p-4 transition-colors hover:border-brand-red">
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl">
                  <img src={o.hero} alt={o.name} loading="lazy" className="h-full w-full object-cover" />
                </div>
                <div className="min-w-0">
                  <p className="font-serif text-base font-bold text-brand-charcoal group-hover:text-brand-red">{o.name}</p>
                  <p className="text-xs text-brand-muted">{o.price}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
